import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  BookOpen, 
  Calendar, 
  FileText, 
  Upload, 
  MessageCircle, 
  CheckCircle, 
  Clock,
  ArrowLeft,
  Play,
  Download,
  Eye,
  Edit,
  Plus,
  X,
  Search,
  Trash2,
  Video,
  Save,
  ChevronDown,
  ChevronRight,
  Megaphone,
  Send,
  AlertCircle,
  Image as ImageIcon,
  Award,
  Loader2,
  User,
  CheckCircle2,
  Circle
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { 
  getBatchById,
  addBatchClass,
  getBatchClasses,
  updateBatchClass,
  deleteBatchClass,
  getBatchEnrollments,
  addBatchStudyMaterial,
  getBatchStudyMaterials,
  deleteBatchStudyMaterial,
  getBatchDoubts,
  getBatchSyllabus,
  saveBatchSyllabus
} from '../../firebase/collections';
import { uploadFile } from '../../firebase/r2storage';
import toast from 'react-hot-toast';
import { collection, addDoc, query, where, orderBy, onSnapshot, serverTimestamp, doc, updateDoc, deleteDoc, getDocs, getDoc, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import teacherBatchSync from '../../services/teacherBatchSync';

// Assignments Tab Component
const AssignmentsTab = ({ batchId, currentUser }) => {
  const [assignments, setAssignments] = useState([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [assignmentFormData, setAssignmentFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    maxMarks: 100,
    instructions: ''
  });

  useEffect(() => {
    let unsubscribe;
    const fetchAssignments = async () => {
      unsubscribe = await loadAssignments();
    };
    fetchAssignments();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [batchId]);

  const loadAssignments = async () => {
    if (!batchId) return;
    
    try {
      const q = query(
        collection(db, 'batchAssignments'),
        where('batchId', '==', batchId),
        orderBy('createdAt', 'desc')
      );
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const assignmentsList = [];
        snapshot.forEach((doc) => {
          assignmentsList.push({ id: doc.id, ...doc.data() });
        });
        setAssignments(assignmentsList);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error loading assignments:', error);
    }
  };

  const handleCreateAssignment = async () => {
    if (!assignmentFormData.title || !assignmentFormData.dueDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const assignmentData = {
        batchId,
        teacherId: currentUser.uid,
        ...assignmentFormData,
        status: 'active',
        createdAt: serverTimestamp()
      };

      if (editingAssignment) {
        await updateDoc(doc(db, 'batchAssignments', editingAssignment.id), assignmentData);
        toast.success('Assignment updated successfully');
      } else {
        await addDoc(collection(db, 'batchAssignments'), assignmentData);
        toast.success('Assignment created successfully');
      }

      setShowAssignmentModal(false);
      setEditingAssignment(null);
      setAssignmentFormData({
        title: '',
        description: '',
        dueDate: '',
        maxMarks: 100,
        instructions: ''
      });
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast.error('Failed to create assignment');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      await deleteDoc(doc(db, 'batchAssignments', assignmentId));
      toast.success('Assignment deleted successfully');
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error('Failed to delete assignment');
    }
  };

  const loadSubmissions = async (assignment) => {
    setLoadingSubmissions(true);
    setSelectedAssignment(assignment);
    setShowSubmissionsModal(true);

    try {
      // Query submissions for this assignment and batch
      const q = query(
        collection(db, 'assignmentSubmissions'),
        where('assignmentId', '==', assignment.id),
        where('batchId', '==', batchId)
        // Note: orderBy removed temporarily due to missing Firestore index
        // Add back after creating composite index for assignmentId + batchId + submittedAt
      );
      
      const snapshot = await getDocs(q);
      const submissionsList = [];
      snapshot.forEach((doc) => {
        submissionsList.push({ id: doc.id, ...doc.data() });
      });
      
      // Fetch student emails for submissions that don't have them
      const enhancedSubmissions = await Promise.all(
        submissionsList.map(async (submission) => {
          // If email is missing, try to fetch from users collection
          if (!submission.studentEmail && submission.studentId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', submission.studentId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  ...submission,
                  studentEmail: userData.email || 'No email available',
                  studentName: submission.studentName || userData.displayName || userData.name || 'Unknown Student'
                };
              }
            } catch (error) {
              console.error('Error fetching user data:', error);
            }
          }
          return submission;
        })
      );
      
      // Sort by submittedAt on client side (newest first)
      enhancedSubmissions.sort((a, b) => {
        const aTime = (a.submittedAt?.toDate ? a.submittedAt.toDate() : (a.submittedAt ? new Date(a.submittedAt) : new Date(0))).getTime();
        const bTime = (b.submittedAt?.toDate ? b.submittedAt.toDate() : (b.submittedAt ? new Date(b.submittedAt) : new Date(0))).getTime();
        return bTime - aTime;
      });
      
      setSubmissions(enhancedSubmissions);
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const formatSubmissionDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const isSubmissionLate = (submittedAt, dueDate) => {
    if (!submittedAt || !dueDate) return false;
    const submissionDate = submittedAt.toDate ? submittedAt.toDate() : new Date(submittedAt);
    const dueDateObj = new Date(dueDate);
    return submissionDate > dueDateObj;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Assignments</h3>
        <button 
          onClick={() => setShowAssignmentModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Assignment
        </button>
      </div>

      {assignments.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Assignments Created</h3>
          <p className="text-gray-600 mb-4">Create your first assignment for this batch</p>
          <button 
            onClick={() => setShowAssignmentModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center mx-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Assignment
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {assignments.map((assignment) => (
            <div key={assignment.id} className="bg-white border rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900">{assignment.title}</h4>
                  <p className="text-gray-600 mt-2">{assignment.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mt-3">
                    <span>Due: {assignment.dueDate}</span>
                    <span>Max Marks: {assignment.maxMarks}</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      assignment.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {assignment.status}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => loadSubmissions(assignment)}
                    className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-lg text-sm flex items-center"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Submissions
                  </button>
                  <button
                    onClick={() => {
                      setEditingAssignment(assignment);
                      setAssignmentFormData({
                        title: assignment.title,
                        description: assignment.description,
                        dueDate: assignment.dueDate,
                        maxMarks: assignment.maxMarks,
                        instructions: assignment.instructions || ''
                      });
                      setShowAssignmentModal(true);
                    }}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteAssignment(assignment.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingAssignment ? 'Edit Assignment' : 'Create Assignment'}
              </h3>
              <button
                onClick={() => {
                  setShowAssignmentModal(false);
                  setEditingAssignment(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={assignmentFormData.title}
                  onChange={(e) => setAssignmentFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={assignmentFormData.description}
                  onChange={(e) => setAssignmentFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
                  <input
                    type="date"
                    value={assignmentFormData.dueDate}
                    onChange={(e) => setAssignmentFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Marks</label>
                  <input
                    type="number"
                    value={assignmentFormData.maxMarks}
                    onChange={(e) => setAssignmentFormData(prev => ({ ...prev, maxMarks: parseInt(e.target.value) || 100 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                <textarea
                  value={assignmentFormData.instructions}
                  onChange={(e) => setAssignmentFormData(prev => ({ ...prev, instructions: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAssignmentModal(false);
                  setEditingAssignment(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAssignment}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {editingAssignment ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submissions Modal */}
      {showSubmissionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold">Assignment Submissions</h3>
                <p className="text-sm text-gray-600">{selectedAssignment?.title}</p>
              </div>
              <button
                onClick={() => {
                  setShowSubmissionsModal(false);
                  setSelectedAssignment(null);
                  setSubmissions([]);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingSubmissions ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 mx-auto mb-4 animate-spin" />
                <p className="text-gray-600">Loading submissions...</p>
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Submissions Yet</h3>
                <p className="text-gray-600">Students haven't submitted this assignment yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary Statistics */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Submission Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{submissions.length}</div>
                      <div className="text-sm text-gray-600">Total Submissions</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {submissions.filter(s => !isSubmissionLate(s.submittedAt, selectedAssignment?.dueDate)).length}
                      </div>
                      <div className="text-sm text-gray-600">On Time</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {submissions.filter(s => isSubmissionLate(s.submittedAt, selectedAssignment?.dueDate)).length}
                      </div>
                      <div className="text-sm text-gray-600">Late Submissions</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-600">{selectedAssignment?.maxMarks || 'N/A'}</div>
                      <div className="text-sm text-gray-600">Max Marks</div>
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-gray-600 text-center">
                    Due Date: {selectedAssignment?.dueDate}
                  </div>
                </div>

                {/* Submissions List */}
                <div className="space-y-4">
                  {submissions.map((submission) => {
                    const isLate = isSubmissionLate(submission.submittedAt, selectedAssignment?.dueDate);
                    
                    return (
                      <div key={submission.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div>
                                <h4 className="font-semibold text-gray-900">
                                  {submission.studentName || 'Unknown Student'}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {submission.studentEmail || 'No email'}
                                </p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                isLate ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                              }`}>
                                {isLate ? 'Late Submission' : 'On Time'}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 mb-2">
                              <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                Submitted: {formatSubmissionDate(submission.submittedAt)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Submission Content */}
                        <div className="bg-gray-50 rounded-lg p-4 mb-3">
                          <h5 className="font-medium text-gray-900 mb-2">Submission Text:</h5>
                          <p className="text-gray-700 whitespace-pre-wrap">
                            {submission.submissionText || 'No text submitted'}
                          </p>
                        </div>

                        {/* Submission Files */}
                        {submission.submissionFiles && submission.submissionFiles.length > 0 && (
                          <div className="mb-3">
                            <h5 className="font-medium text-gray-900 mb-2">Attached Files:</h5>
                            <div className="space-y-2">
                              {submission.submissionFiles.map((file, index) => (
                                <div key={index} className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                                  <div className="flex items-center">
                                    <FileText className="w-4 h-4 text-blue-600 mr-2" />
                                    <span className="text-sm text-gray-900">{file.name}</span>
                                  </div>
                                  <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                                  >
                                    <Download className="w-4 h-4 mr-1" />
                                    Download
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Doubts Tab Component
const DoubtsTab = ({ batchId, currentUser }) => {
  const [doubts, setDoubts] = useState([]);
  const [answeringDoubt, setAnsweringDoubt] = useState(null);
  const [answerText, setAnswerText] = useState('');

  useEffect(() => {
    loadDoubts();
  }, [batchId]);

  const loadDoubts = async () => {
    if (!batchId) return;
    
    try {
      // Use getBatchDoubts function with URL conversion
      const result = await getBatchDoubts(batchId);
      
      if (result.success) {
        setDoubts(result.data);
      } else {
        console.error('Error loading doubts:', result.error);
        setDoubts([]);
      }
    } catch (error) {
      console.error('Error loading doubts:', error);
      setDoubts([]);
    }
  };

  const handleAnswerDoubt = async (doubtId) => {
    if (!answerText.trim()) {
      toast.error('Please enter an answer');
      return;
    }

    try {
      await updateDoc(doc(db, 'batchDoubts', doubtId), {
        answer: answerText,
        answeredBy: currentUser.uid,
        answeredAt: serverTimestamp(),
        status: 'answered'
      });

      toast.success('Doubt answered successfully');
      setAnsweringDoubt(null);
      setAnswerText('');
    } catch (error) {
      console.error('Error answering doubt:', error);
      toast.error('Failed to answer doubt');
    }
  };

  const handleResolveDoubt = async (doubtId) => {
    if (!window.confirm('Are you sure you want to mark this doubt as resolved?')) return;

    try {
      await updateDoc(doc(db, 'batchDoubts', doubtId), {
        status: 'resolved',
        resolvedBy: currentUser.uid,
        resolvedAt: serverTimestamp()
      });

      toast.success('Doubt marked as resolved');
    } catch (error) {
      console.error('Error resolving doubt:', error);
      toast.error('Failed to resolve doubt');
    }
  };

  const handleDeleteDoubt = async (doubtId) => {
    if (!window.confirm('Are you sure you want to delete this doubt? This action cannot be undone.')) return;

    try {
      await deleteDoc(doc(db, 'batchDoubts', doubtId));
      toast.success('Doubt deleted successfully');
    } catch (error) {
      console.error('Error deleting doubt:', error);
      
      if (error.code === 'permission-denied') {
        toast.error('Permission denied: You do not have permission to delete this doubt');
      } else if (error.code === 'not-found') {
        toast.error('Doubt not found');
      } else {
        toast.error('Failed to delete doubt');
      }
    }
  };

  const handleReopenDoubt = async (doubtId) => {
    if (!window.confirm('Are you sure you want to reopen this doubt?')) return;

    try {
      await updateDoc(doc(db, 'batchDoubts', doubtId), {
        status: 'pending',
        reopenedBy: currentUser.uid,
        reopenedAt: serverTimestamp()
      });

      toast.success('Doubt reopened successfully');
    } catch (error) {
      console.error('Error reopening doubt:', error);
      toast.error('Failed to reopen doubt');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Student Doubts</h3>
        <div className="flex items-center space-x-4 text-sm text-gray-600">
          <span>{doubts.filter(d => d.status === 'pending').length} pending</span>
          <span>{doubts.filter(d => d.status === 'answered').length} answered</span>
          <span>{doubts.filter(d => d.status === 'resolved').length} resolved</span>
        </div>
      </div>

      {doubts.length === 0 ? (
        <div className="text-center py-12">
          <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Doubts Yet</h3>
          <p className="text-gray-600">Students haven't asked any questions yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {doubts.map((doubt) => (
            <div key={doubt.id} className="bg-white border rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-semibold text-gray-900">{doubt.studentName}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      doubt.status === 'resolved' ? 'bg-gray-100 text-gray-800' :
                      doubt.status === 'answered' ? 'bg-green-100 text-green-800' : 
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {doubt.status || 'pending'}
                    </span>
                  </div>
                  <p className="text-gray-700 mb-3">{doubt.question}</p>
                  {doubt.imageUrl && (
                    <img src={doubt.imageUrl} alt="Doubt" className="max-w-xs rounded-lg mb-3" />
                  )}
                  
                  {doubt.answer && (
                    <div className="bg-blue-50 p-4 rounded-lg mt-4">
                      <h5 className="font-medium text-blue-900 mb-2">Your Answer:</h5>
                      <p className="text-blue-800">{doubt.answer}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                {answeringDoubt === doubt.id ? (
                  <div className="space-y-3">
                    <textarea
                      value={answerText}
                      onChange={(e) => setAnswerText(e.target.value)}
                      placeholder="Type your answer here..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="4"
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleAnswerDoubt(doubt.id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Submit Answer
                      </button>
                      <button
                        onClick={() => {
                          setAnsweringDoubt(null);
                          setAnswerText('');
                        }}
                        className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {/* Answer Button - Only show for pending doubts */}
                    {doubt.status === 'pending' && (
                      <button
                        onClick={() => setAnsweringDoubt(doubt.id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Answer Doubt
                      </button>
                    )}
                    
                    {/* Resolve Button - Show for pending and answered doubts */}
                    {(doubt.status === 'pending' || doubt.status === 'answered') && (
                      <button
                        onClick={() => handleResolveDoubt(doubt.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark Resolved
                      </button>
                    )}
                    
                    {/* Reopen Button - Only show for resolved doubts */}
                    {doubt.status === 'resolved' && (
                      <button
                        onClick={() => handleReopenDoubt(doubt.id)}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center"
                      >
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Reopen
                      </button>
                    )}
                    
                    {/* Delete Button - Always available */}
                    <button
                      onClick={() => handleDeleteDoubt(doubt.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Attendance Tab Component
const AttendanceTab = ({ batchId, students, currentUser, studentsLoading }) => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [markingAttendance, setMarkingAttendance] = useState(false);
  const [attendanceData, setAttendanceData] = useState({});

  useEffect(() => {
    loadAttendanceRecords();
  }, [batchId, selectedDate]);

  const loadAttendanceRecords = async () => {
    if (!batchId) return;
    
    try {
      const q = query(
        collection(db, 'batchAttendance'),
        where('batchId', '==', batchId),
        where('date', '==', selectedDate)
      );
      
      const snapshot = await getDocs(q);
      const records = {};
      snapshot.forEach((doc) => {
        const data = doc.data();
        records[data.studentId] = data.status;
      });
      setAttendanceRecords(records);
      setAttendanceData(records);
    } catch (error) {
      console.error('Error loading attendance:', error);
    }
  };

  const handleMarkAttendance = async () => {
    setMarkingAttendance(true);
    
    try {
      const batch = [];
      
      for (const student of students) {
        const status = attendanceData[student.userId] || 'absent';
        
        // Check if record exists
        const existingQuery = query(
          collection(db, 'batchAttendance'),
          where('batchId', '==', batchId),
          where('studentId', '==', student.userId),
          where('date', '==', selectedDate)
        );
        
        const existingSnapshot = await getDocs(existingQuery);
        
        if (existingSnapshot.empty) {
          // Create new record
          batch.push(addDoc(collection(db, 'batchAttendance'), {
            batchId,
            studentId: student.userId,
            studentName: student.studentName || student.userData?.displayName || student.userData?.name || student.name || student.displayName || 'Unknown Student',
            date: selectedDate,
            status,
            markedBy: currentUser.uid,
            markedAt: serverTimestamp()
          }));
        } else {
          // Update existing record
          const docId = existingSnapshot.docs[0].id;
          batch.push(updateDoc(doc(db, 'batchAttendance', docId), {
            status,
            markedBy: currentUser.uid,
            markedAt: serverTimestamp()
          }));
        }
      }
      
      await Promise.all(batch);
      toast.success('Attendance marked successfully');
      loadAttendanceRecords();
    } catch (error) {
      console.error('Error marking attendance:', error);
      toast.error('Failed to mark attendance');
    } finally {
      setMarkingAttendance(false);
    }
  };

  const handleAttendanceChange = (studentId, status) => {
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Student Attendance</h3>
        <div className="flex items-center space-x-4">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button 
            onClick={handleMarkAttendance}
            disabled={markingAttendance}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
          >
            {markingAttendance ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Mark Attendance
          </button>
        </div>
      </div>

      {studentsLoading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 text-blue-600 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Students...</h3>
          <p className="text-gray-600">Please wait while we load the student list</p>
        </div>
      ) : students.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Enrolled</h3>
          <p className="text-gray-600">No students are enrolled in this batch yet</p>
        </div>
      ) : (
        <div>
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Student</th>
                  <th className="px-6 py-3 text-center text-sm font-medium text-gray-900">Present</th>
                  <th className="px-6 py-3 text-center text-sm font-medium text-gray-900">Absent</th>
                  <th className="px-6 py-3 text-center text-sm font-medium text-gray-900">Late</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {students.map((student) => (
                  <tr key={student.userId}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="font-medium">
                          {student.studentName || student.userData?.displayName || student.userData?.name || student.name || student.displayName || 'Unknown Student'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="radio"
                        name={`attendance-${student.userId}`}
                        checked={attendanceData[student.userId] === 'present'}
                        onChange={() => handleAttendanceChange(student.userId, 'present')}
                        className="w-4 h-4 text-green-600"
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="radio"
                        name={`attendance-${student.userId}`}
                        checked={attendanceData[student.userId] === 'absent'}
                        onChange={() => handleAttendanceChange(student.userId, 'absent')}
                        className="w-4 h-4 text-red-600"
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="radio"
                        name={`attendance-${student.userId}`}
                        checked={attendanceData[student.userId] === 'late'}
                        onChange={() => handleAttendanceChange(student.userId, 'late')}
                        className="w-4 h-4 text-yellow-600"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

// Test Results Tab Component
const TestResultsTab = ({ batchId, students, currentUser, studentsLoading }) => {
  const [coachingTests, setCoachingTests] = useState([]);
  const [studentResults, setStudentResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [batchData, setBatchData] = useState(null);

  useEffect(() => {
    if (batchId && students.length > 0) {
      loadBatchAndTests();
    }
  }, [batchId, students]);

  const loadBatchAndTests = async () => {
    try {
      setLoading(true);
      
      // Get batch data to find coachingId
      const batchResult = await getBatchById(batchId);
      if (batchResult.success) {
        setBatchData(batchResult.data);
        await loadCoachingTests(batchResult.data.coachingId);
      }
    } catch (error) {
      console.error('Error loading batch and tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCoachingTests = async (coachingId) => {
    if (!coachingId) return;
    
    try {
      // Load tests created by this coaching
      const testsQuery = query(
        collection(db, 'tests'),
        where('coachingId', '==', coachingId),
        where('status', '==', 'published'),
        orderBy('createdAt', 'desc')
      );
      
      const testsSnapshot = await getDocs(testsQuery);
      const testsList = [];
      testsSnapshot.forEach((doc) => {
        testsList.push({ id: doc.id, ...doc.data() });
      });
      
      setCoachingTests(testsList);
      
      // Load results for all students and all tests
      await loadAllStudentResults(testsList);
      
    } catch (error) {
      console.error('Error loading coaching tests:', error);
      // Fallback: try loading tests linked to this batch
      await loadBatchLinkedTests();
    }
  };

  const loadBatchLinkedTests = async () => {
    try {
      // Load tests linked to this specific batch via batchTests collection
      const batchTestsQuery = query(
        collection(db, 'batchTests'),
        where('batchId', '==', batchId)
      );
      
      const batchTestsSnapshot = await getDocs(batchTestsQuery);
      const testIds = [];
      batchTestsSnapshot.forEach((doc) => {
        testIds.push(doc.data().testId);
      });
      
      if (testIds.length > 0) {
        // Load full test data for each test ID
        const testsList = [];
        for (const testId of testIds) {
          try {
            const testDoc = await getDoc(doc(db, 'tests', testId));
            if (testDoc.exists()) {
              testsList.push({ id: testDoc.id, ...testDoc.data() });
            }
          } catch (error) {
            console.error('Error loading test:', testId, error);
          }
        }
        
        setCoachingTests(testsList);
        await loadAllStudentResults(testsList);
      }
    } catch (error) {
      console.error('Error loading batch linked tests:', error);
    }
  };

  const loadAllStudentResults = async (testsList) => {
    const resultsMap = {};
    
    // Initialize results map for each student
    students.forEach(student => {
      resultsMap[student.userId] = {};
    });
    
    // Load results for each test
    for (const test of testsList) {
      try {
        const resultsQuery = query(
          collection(db, 'testResults'),
          where('testId', '==', test.id),
          where('status', '==', 'evaluated')
        );
        
        const resultsSnapshot = await getDocs(resultsQuery);
        resultsSnapshot.forEach((doc) => {
          const result = doc.data();
          if (resultsMap[result.userId]) {
            resultsMap[result.userId][test.id] = {
              ...result,
              testName: test.title,
              testDate: test.createdAt?.toDate?.() || new Date(test.createdAt),
              maxMarks: test.totalMarks || test.maxMarks || 100
            };
          }
        });
      } catch (error) {
        console.error('Error loading results for test:', test.id, error);
      }
    }
    
    setStudentResults(resultsMap);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date.toDate) date = date.toDate();
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getScoreColor = (score, maxMarks) => {
    const percentage = (score / maxMarks) * 100;
    if (percentage >= 80) return 'text-green-600 bg-green-50';
    if (percentage >= 60) return 'text-blue-600 bg-blue-50';
    if (percentage >= 40) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Student Test Results</h3>
        <div className="text-sm text-gray-600">
          {coachingTests.length} coaching tests • {students.length} students
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 text-blue-600 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Test Results...</h3>
          <p className="text-gray-600">Please wait while we load student test data</p>
        </div>
      ) : coachingTests.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Coaching Tests Found</h3>
          <p className="text-gray-600">No tests have been created by your coaching yet</p>
        </div>
      ) : students.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Enrolled</h3>
          <p className="text-gray-600">No students are enrolled in this batch</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Tests Header */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Available Coaching Tests</h4>
            <div className="flex flex-wrap gap-2">
              {coachingTests.map((test, index) => (
                <span key={test.id} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  {index + 1}. {test.title}
                </span>
              ))}
            </div>
          </div>

          {/* Student Results Table */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-900 sticky left-0 bg-gray-50">
                      Student Name
                    </th>
                    {coachingTests.map((test) => (
                      <th key={test.id} className="px-4 py-3 text-center text-sm font-medium text-gray-900 min-w-[120px]">
                        <div className="flex flex-col">
                          <span className="font-semibold">{test.title}</span>
                          <span className="text-xs text-gray-500 font-normal">
                            {formatDate(test.createdAt)}
                          </span>
                        </div>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-900">
                      Average
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {students.map((student) => {
                    const studentTestResults = studentResults[student.userId] || {};
                    const completedTests = Object.values(studentTestResults);
                    const averageScore = completedTests.length > 0 
                      ? completedTests.reduce((sum, result) => sum + ((result.score / result.maxMarks) * 100), 0) / completedTests.length
                      : 0;

                    return (
                      <tr key={student.userId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 sticky left-0 bg-white">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                              <User className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">
                                {student.studentName || student.userData?.displayName || student.userData?.name || 'Unknown Student'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {student.studentEmail || student.userData?.email || 'No email'}
                              </div>
                              <div className="text-xs text-gray-400">
                                {completedTests.length}/{coachingTests.length} tests completed
                              </div>
                            </div>
                          </div>
                        </td>
                        {coachingTests.map((test) => {
                          const result = studentTestResults[test.id];
                          return (
                            <td key={test.id} className="px-4 py-4 text-center">
                              {result ? (
                                <div className={`inline-flex flex-col items-center px-3 py-2 rounded-lg ${getScoreColor(result.score, result.maxMarks)}`}>
                                  <span className="font-semibold">
                                    {result.score}/{result.maxMarks}
                                  </span>
                                  <span className="text-xs">
                                    {((result.score / result.maxMarks) * 100).toFixed(1)}%
                                  </span>
                                  <span className="text-xs opacity-75">
                                    {formatDate(result.submittedAt || result.createdAt)}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">Not attempted</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-4 py-4 text-center">
                          {completedTests.length > 0 ? (
                            <div className={`inline-flex flex-col items-center px-3 py-2 rounded-lg ${
                              averageScore >= 80 ? 'text-green-600 bg-green-50' :
                              averageScore >= 60 ? 'text-blue-600 bg-blue-50' :
                              averageScore >= 40 ? 'text-yellow-600 bg-yellow-50' :
                              'text-red-600 bg-red-50'
                            }`}>
                              <span className="font-semibold">{averageScore.toFixed(1)}%</span>
                              <span className="text-xs opacity-75">
                                {completedTests.length} test{completedTests.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">No data</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Total Tests</p>
                  <p className="text-2xl font-bold text-blue-900">{coachingTests.length}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Avg Completion</p>
                  <p className="text-2xl font-bold text-green-900">
                    {students.length > 0 ? (
                      (Object.values(studentResults).reduce((sum, results) => 
                        sum + Object.keys(results).length, 0) / (students.length * coachingTests.length) * 100).toFixed(1)
                    ) : 0}%
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600">Class Average</p>
                  <p className="text-2xl font-bold text-yellow-900">
                    {(() => {
                      const allScores = Object.values(studentResults).flatMap(results => 
                        Object.values(results).map(result => (result.score / result.maxMarks) * 100)
                      );
                      return allScores.length > 0 ? (allScores.reduce((sum, score) => sum + score, 0) / allScores.length).toFixed(1) : 0;
                    })()}%
                  </p>
                </div>
                <Award className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">Top Performer</p>
                  <p className="text-lg font-bold text-purple-900">
                    {(() => {
                      let topStudent = null;
                      let topAverage = 0;
                      
                      students.forEach(student => {
                        const results = Object.values(studentResults[student.userId] || {});
                        if (results.length > 0) {
                          const avg = results.reduce((sum, result) => sum + ((result.score / result.maxMarks) * 100), 0) / results.length;
                          if (avg > topAverage) {
                            topAverage = avg;
                            topStudent = student.studentName || student.userData?.displayName || 'Unknown';
                          }
                        }
                      });
                      
                      return topStudent ? `${topStudent.split(' ')[0]} (${topAverage.toFixed(1)}%)` : 'No data';
                    })()}
                  </p>
                </div>
                <User className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Syllabus Tab Component
const SyllabusTab = ({ batchId }) => {
  const [syllabus, setSyllabus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [expandedTopics, setExpandedTopics] = useState({});

  useEffect(() => {
    const loadSyllabus = async () => {
      if (!batchId) return;
      setLoading(true);
      const result = await getBatchSyllabus(batchId);
      if (result.success && result.data) {
        setSyllabus(result.data);
      }
      setLoading(false);
    };
    loadSyllabus();
  }, [batchId]);

  const handleToggleSubtopic = async (subjectIndex, topicIndex, subtopicIndex) => {
    const newSyllabus = { ...syllabus };
    const subtopic = newSyllabus.subjects[subjectIndex].topics[topicIndex].subtopics[subtopicIndex];
    
    subtopic.isDone = !subtopic.isDone;
    subtopic.doneAt = subtopic.isDone ? new Date().toISOString() : null;

    // Auto-update topic status
    const topic = newSyllabus.subjects[subjectIndex].topics[topicIndex];
    const allSubtopicsDone = topic.subtopics.length > 0 && topic.subtopics.every(s => s.isDone);
    
    topic.isDone = allSubtopicsDone;
    if (allSubtopicsDone) {
      const latestDoneAt = topic.subtopics.reduce((latest, s) => {
        if (!latest || (s.doneAt && new Date(s.doneAt) > new Date(latest))) {
          return s.doneAt;
        }
        return latest;
      }, null);
      topic.doneAt = latestDoneAt;
    } else {
      topic.doneAt = null;
    }

    setSyllabus(newSyllabus);
    
    try {
      await saveBatchSyllabus(batchId, newSyllabus.subjects);
      toast.success('Progress updated');
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const calculateSubjectProgress = (subject) => {
    let total = 0;
    let completed = 0;
    subject.topics.forEach(topic => {
      if (topic.subtopics.length === 0) {
        total += 1;
        if (topic.isDone) completed += 1;
      } else {
        total += topic.subtopics.length;
        completed += topic.subtopics.filter(s => s.isDone).length;
      }
    });
    return total === 0 ? 0 : Math.round((completed / total) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!syllabus || !syllabus.subjects || syllabus.subjects.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No syllabus available for this batch yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4">
        {syllabus.subjects.map((subject, sIdx) => {
          const progress = calculateSubjectProgress(subject);
          const isExpanded = expandedSubjects[sIdx];

          return (
            <div key={sIdx} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedSubjects(prev => ({ ...prev, [sIdx]: !prev[sIdx] }))}
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">{subject.name}</h3>
                  </div>
                  <div className="mt-3 flex items-center space-x-4">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-600 transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-blue-600">{progress}%</span>
                  </div>
                </div>
                <div className="ml-4">
                  {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-100 p-4 bg-gray-50/30">
                  <div className="space-y-3">
                    {subject.topics.map((topic, tIdx) => {
                      const topicKey = `${sIdx}-${tIdx}`;
                      const isTopicExpanded = expandedTopics[topicKey];

                      return (
                        <div key={tIdx} className="bg-white border border-gray-100 rounded-lg overflow-hidden">
                          <div 
                            className="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                            onClick={() => setExpandedTopics(prev => ({ ...prev, [topicKey]: !prev[topicKey] }))}
                          >
                            <div className="flex items-center space-x-3">
                              {topic.isDone ? (
                                <CheckCircle2 className="w-5 h-5 text-green-500" />
                              ) : (
                                <Circle className="w-5 h-5 text-gray-300" />
                              )}
                              <div className="flex flex-col">
                                {topic.isDone && topic.doneAt && (
                                  <span className="text-[10px] text-green-600 font-medium mb-0.5">
                                    Done: {new Date(topic.doneAt).toLocaleDateString('en-IN', {
                                      day: 'numeric',
                                      month: 'short',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                )}
                                <span className={`font-semibold ${topic.isDone ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                  {topic.name}
                                </span>
                              </div>
                            </div>
                            {topic.subtopics.length > 0 && (
                              <div>
                                {isTopicExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                              </div>
                            )}
                          </div>

                          {isTopicExpanded && topic.subtopics.length > 0 && (
                            <div className="bg-gray-50/50 px-4 py-2 space-y-2 border-t border-gray-50">
                              {topic.subtopics.map((subtopic, stIdx) => (
                                <div 
                                  key={stIdx} 
                                  className="flex items-center justify-between p-2 rounded-lg cursor-pointer hover:bg-white group"
                                  onClick={() => handleToggleSubtopic(sIdx, tIdx, stIdx)}
                                >
                                  <div className="flex items-center space-x-3">
                                    {subtopic.isDone ? (
                                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                                    ) : (
                                      <Circle className="w-4 h-4 text-gray-300" />
                                    )}
                                    <span className={`text-sm ${subtopic.isDone ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                                      {subtopic.name}
                                    </span>
                                  </div>
                                  <span className="text-[10px] font-medium text-blue-500 opacity-0 group-hover:opacity-100">Click to toggle</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TeacherBatchManagement = () => {
  const { batchId } = useParams();
  const { currentUser, userData, userProfile } = useRealtime();
  const [activeTab, setActiveTab] = useState('classes');
  const [batchData, setBatchData] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [batchClasses, setBatchClasses] = useState([]);
  const [studyMaterials, setStudyMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showClassModal, setShowClassModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [selectedClassForMaterial, setSelectedClassForMaterial] = useState(null);
  
  // Form states
  const [classFormData, setClassFormData] = useState({
    subject: '',
    date: '',
    time: '',
    duration: 60,
    zoomLink: '',
    description: ''
  });
  
  // Upload states
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);



  useEffect(() => {
    loadBatchData();
  }, [batchId]);

  const loadBatchData = async () => {
    if (!batchId) {
      setLoading(false);
      setStudentsLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setStudentsLoading(true);
      
      // Load batch details
      const batchResult = await getBatchById(batchId);
      if (batchResult.success) {
        setBatchData(batchResult.data);
      }
      
      // Load batch classes
      await loadBatchClasses();
      
      // Load enrollments
      const enrollmentsResult = await getBatchEnrollments(batchId);
      
      if (enrollmentsResult.success && enrollmentsResult.data && enrollmentsResult.data.length > 0) {
        // Map enrollment data
        const mappedStudents = enrollmentsResult.data.map(enrollment => ({
          ...enrollment,
          userId: enrollment.userId || enrollment.id,
          userData: enrollment.userData || enrollment,
          studentName: enrollment.userData?.fullName || enrollment.userData?.name || enrollment.userData?.displayName || enrollment.email || 'Unknown',
          studentEmail: enrollment.userData?.email || enrollment.email || ''
        }));
        setStudents(mappedStudents);
      } else {
        // Fallback to teacherBatchSync method
        try {
          const studentsFromSync = await teacherBatchSync.getBatchStudents(batchId);
          const transformedStudents = studentsFromSync.map(student => ({
            userId: student.id,
            userData: student,
            studentName: student.displayName || student.name || student.email || 'Unknown',
            studentEmail: student.email || '',
            ...student
          }));
          setStudents(transformedStudents);
        } catch (syncError) {
          console.error('Error loading students:', syncError);
          setStudents([]);
        }
      }
      
      setStudentsLoading(false);
    } catch (error) {
      console.error('Error loading batch data:', error);
      toast.error('Failed to load batch data');
    } finally {
      setLoading(false);
    }
  };

  const loadBatchClasses = async () => {
    if (!batchId) return;
    
    try {
      const result = await getBatchClasses(batchId);
      if (result.success) {
        setBatchClasses(result.data);
      }
    } catch (error) {
      console.error('Error loading batch classes:', error);
    }
  };

  const loadStudyMaterials = async (classId) => {
    if (!classId) return;
    
    try {
      const result = await getBatchStudyMaterials(classId);
      if (result.success) {
        setStudyMaterials(result.data);
      }
    } catch (error) {
      console.error('Error loading study materials:', error);
    }
  };

  // Class management functions
  const handleAddClass = async () => {
    if (!classFormData.subject || !classFormData.date || !classFormData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const classData = {
        batchId,
        teacherId: currentUser.uid,
        teacherName: userProfile?.displayName || currentUser.displayName || 'Teacher',
        ...classFormData,
        status: 'scheduled',
        createdAt: serverTimestamp()
      };

      if (editingClass) {
        const result = await updateBatchClass(editingClass.id, classData);
        if (result.success) {
          toast.success('Class updated successfully');
          setEditingClass(null);
        } else {
          toast.error('Failed to update class');
        }
      } else {
        const result = await addBatchClass(classData);
        if (result.success) {
          toast.success('Class scheduled successfully');
        } else {
          toast.error('Failed to schedule class');
        }
      }

      setShowClassModal(false);
      setClassFormData({
        subject: '',
        date: '',
        time: '',
        duration: 60,
        zoomLink: '',
        description: ''
      });
      await loadBatchClasses();
    } catch (error) {
      console.error('Error managing class:', error);
      toast.error('Failed to manage class');
    }
  };

  const handleEditClass = (classItem) => {
    setEditingClass(classItem);
    setClassFormData({
      subject: classItem.subject || '',
      date: classItem.date || '',
      time: classItem.time || '',
      duration: classItem.duration || 60,
      zoomLink: classItem.zoomLink || '',
      description: classItem.description || ''
    });
    setShowClassModal(true);
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;

    try {
      const result = await deleteBatchClass(classId);
      if (result.success) {
        toast.success('Class deleted successfully');
        await loadBatchClasses();
      } else {
        toast.error('Failed to delete class');
      }
    } catch (error) {
      console.error('Error deleting class:', error);
      toast.error('Failed to delete class');
    }
  };

  const handleRecordingUpload = async (e, classId) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 500 * 1024 * 1024) { // 500MB limit
      toast.error('File size should be less than 500MB');
      return;
    }

    try {
      setUploadingRecording(true);
      const fileName = `batch-recordings/${classId}/${Date.now()}_${file.name}`;
      const result = await uploadFile(file, fileName);
      
      if (result.success) {
        await updateBatchClass(classId, { recordingUrl: result.url, status: 'completed' });
        toast.success('Recording uploaded successfully');
        await loadBatchClasses();
      } else {
        toast.error('Failed to upload recording');
      }
    } catch (error) {
      console.error('Error uploading recording:', error);
      toast.error('Failed to upload recording');
    } finally {
      setUploadingRecording(false);
    }
  };

  const handleDeleteRecording = async (classId, recordingUrl) => {
    if (!window.confirm('Are you sure you want to delete this recording? This action cannot be undone.')) {
      return;
    }

    try {
      // Remove recording URL from class
      await updateBatchClass(classId, { recordingUrl: null });
      toast.success('Recording deleted successfully');
      await loadBatchClasses();
      
      // Note: The actual file in storage will remain but won't be accessible via the app
      // To fully delete from storage, you'd need to implement deleteFile function
    } catch (error) {
      console.error('Error deleting recording:', error);
      toast.error('Failed to delete recording');
    }
  };

  const handleMaterialUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingMaterial(true);
      const fileName = `batch-materials/${selectedClassForMaterial.id}/${Date.now()}_${file.name}`;
      const result = await uploadFile(file, fileName);
      
      if (result.success) {
        await addBatchStudyMaterial(selectedClassForMaterial.id, {
          title: file.name,
          url: result.url,
          type: file.type,
          size: file.size
        });
        toast.success('Study material uploaded successfully');
        setShowMaterialModal(false);
        setSelectedClassForMaterial(null);
        await loadStudyMaterials(selectedClassForMaterial.id);
      } else {
        toast.error('Failed to upload material');
      }
    } catch (error) {
      console.error('Error uploading material:', error);
      toast.error('Failed to upload material');
    } finally {
      setUploadingMaterial(false);
    }
  };

  const tabs = [
    { id: 'classes', name: 'Classes & Materials', icon: Calendar },
    { id: 'assignments', name: 'Assignments', icon: FileText },
    { id: 'syllabus', name: 'Syllabus', icon: BookOpen },
    { id: 'doubts', name: 'Doubts', icon: MessageCircle },
    { id: 'attendance', name: 'Attendance', icon: CheckCircle },
    { id: 'results', name: 'Test Results', icon: Users }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'classes':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Classes & Materials</h3>
              <button 
                onClick={() => setShowClassModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Schedule Class
              </button>
            </div>

            {batchClasses.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Classes Scheduled</h3>
                <p className="text-gray-600 mb-4">Schedule your first class to get started</p>
                <button 
                  onClick={() => setShowClassModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center mx-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Class
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {batchClasses.map((classItem) => (
                  <div key={classItem.id} className="bg-white border rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-900">{classItem.subject}</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-2">
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {classItem.date}
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {classItem.time} ({classItem.duration} min)
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            classItem.status === 'completed' ? 'bg-green-100 text-green-800' :
                            classItem.status === 'ongoing' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {classItem.status || 'scheduled'}
                          </span>
                        </div>
                        {classItem.description && (
                          <p className="text-gray-600 mt-2">{classItem.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditClass(classItem)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="Edit Class"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClass(classItem.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete Class"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Class Actions */}
                    <div className="flex flex-wrap gap-3 mb-4">
                      {classItem.zoomLink && (
                        <a
                          href={classItem.zoomLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center text-sm"
                        >
                          <Video className="w-4 h-4 mr-2" />
                          Join Class
                        </a>
                      )}
                      
                      {!classItem.recordingUrl && (
                        <label className="cursor-pointer px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center text-sm">
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingRecording ? 'Uploading...' : 'Upload Recording'}
                          <input
                            type="file"
                            accept="video/*"
                            onChange={(e) => handleRecordingUpload(e, classItem.id)}
                            className="hidden"
                            disabled={uploadingRecording}
                          />
                        </label>
                      )}

                      {classItem.recordingUrl && (
                        <>
                          <a
                            href={classItem.recordingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center text-sm"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            View Recording
                          </a>
                          
                          <label className="cursor-pointer px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center text-sm">
                            <Upload className="w-4 h-4 mr-2" />
                            {uploadingRecording ? 'Uploading...' : 'Reupload'}
                            <input
                              type="file"
                              accept="video/*"
                              onChange={(e) => handleRecordingUpload(e, classItem.id)}
                              className="hidden"
                              disabled={uploadingRecording}
                            />
                          </label>
                          
                          <button
                            onClick={() => handleDeleteRecording(classItem.id, classItem.recordingUrl)}
                            className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center text-sm"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </button>
                        </>
                      )}

                      <button
                        onClick={() => {
                          setSelectedClassForMaterial(classItem);
                          setShowMaterialModal(true);
                          loadStudyMaterials(classItem.id);
                        }}
                        className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center text-sm"
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        Manage Materials
                      </button>
                    </div>

                    {/* Study Materials Preview */}
                    <div className="border-t pt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Study Materials</h5>
                      <StudyMaterialsList classId={classItem.id} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'assignments':
        return <AssignmentsTab batchId={batchId} currentUser={currentUser} />;

      case 'syllabus':
        return <SyllabusTab batchId={batchId} />;

      case 'doubts':
        return <DoubtsTab batchId={batchId} currentUser={currentUser} />;

      case 'attendance':
        return <AttendanceTab batchId={batchId} students={students} currentUser={currentUser} studentsLoading={studentsLoading} />;

      case 'results':
        return <TestResultsTab batchId={batchId} students={students} currentUser={currentUser} studentsLoading={studentsLoading} />;

      default:
        return null;
    }
  };

  // Study Materials List Component
  const StudyMaterialsList = ({ classId }) => {
    const [materials, setMaterials] = useState([]);
    const [loadingMaterials, setLoadingMaterials] = useState(false);

    useEffect(() => {
      if (classId) {
        loadClassMaterials();
      }
    }, [classId]);

    const loadClassMaterials = async () => {
      setLoadingMaterials(true);
      try {
        const result = await getBatchStudyMaterials(classId);
        if (result.success) {
          setMaterials(result.data);
        }
      } catch (error) {
        console.error('Error loading materials:', error);
      } finally {
        setLoadingMaterials(false);
      }
    };

    const handleDeleteMaterial = async (materialId) => {
      if (!window.confirm('Are you sure you want to delete this material?')) return;

      try {
        const result = await deleteBatchStudyMaterial(materialId);
        if (result.success) {
          toast.success('Material deleted successfully');
          loadClassMaterials();
        } else {
          toast.error('Failed to delete material');
        }
      } catch (error) {
        console.error('Error deleting material:', error);
        toast.error('Failed to delete material');
      }
    };

    if (loadingMaterials) {
      return <div className="text-sm text-gray-500">Loading materials...</div>;
    }

    if (materials.length === 0) {
      return <div className="text-sm text-gray-500">No materials uploaded yet</div>;
    }

    return (
      <div className="space-y-2">
        {materials.map((material) => (
          <div key={material.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div className="flex items-center space-x-2">
              <FileText className="w-4 h-4 text-gray-600" />
              <span className="text-sm text-gray-700">{material.title}</span>
            </div>
            <div className="flex items-center space-x-2">
              <a
                href={material.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800"
              >
                <Download className="w-4 h-4" />
              </a>
              <button
                onClick={() => handleDeleteMaterial(material.id)}
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading batch data...</p>
        </div>
      </div>
    );
  }

  if (!batchData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Batch Not Found</h3>
          <p className="text-gray-600 mb-4">The batch you're looking for doesn't exist or you don't have access to it.</p>
          <Link
            to="/teacher/dashboard"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">


        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <Link
              to="/teacher/dashboard"
              className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Link>
          </div>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {batchData.name}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {batchData.subject} • {students.length} students
              </p>
            </div>
            <div className="flex space-x-3">
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          {renderTabContent()}
        </div>

        {/* Class Modal */}
        {showClassModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  {editingClass ? 'Edit Class' : 'Schedule New Class'}
                </h3>
                <button
                  onClick={() => {
                    setShowClassModal(false);
                    setEditingClass(null);
                    setClassFormData({
                      subject: '',
                      date: '',
                      time: '',
                      duration: 60,
                      zoomLink: '',
                      description: ''
                    });
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={classFormData.subject}
                    onChange={(e) => setClassFormData(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter class subject"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      value={classFormData.date}
                      onChange={(e) => setClassFormData(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time *
                    </label>
                    <input
                      type="time"
                      value={classFormData.time}
                      onChange={(e) => setClassFormData(prev => ({ ...prev, time: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    value={classFormData.duration}
                    onChange={(e) => setClassFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 60 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="15"
                    max="300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zoom Link (Optional)
                  </label>
                  <input
                    type="url"
                    value={classFormData.zoomLink}
                    onChange={(e) => setClassFormData(prev => ({ ...prev, zoomLink: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://zoom.us/j/..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={classFormData.description}
                    onChange={(e) => setClassFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Class description or agenda"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowClassModal(false);
                    setEditingClass(null);
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddClass}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingClass ? 'Update Class' : 'Schedule Class'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Material Modal */}
        {showMaterialModal && selectedClassForMaterial && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Study Materials - {selectedClassForMaterial.subject}
                </h3>
                <button
                  onClick={() => {
                    setShowMaterialModal(false);
                    setSelectedClassForMaterial(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Study Material
                </label>
                <input
                  type="file"
                  onChange={handleMaterialUpload}
                  disabled={uploadingMaterial}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png"
                />
                {uploadingMaterial && (
                  <p className="text-sm text-gray-500 mt-2">Uploading...</p>
                )}
              </div>

              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Uploaded Materials</h4>
                <StudyMaterialsList classId={selectedClassForMaterial.id} />
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default TeacherBatchManagement;